import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  Signal,
  signal,
  ViewChild,
  WritableSignal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  BottomNavbarComponent,
  OrAvatarComponent,
  OrIconComponent,
  PageLayoutComponent,
  TitileNavbarComponent
} from '@ui-components';
import { Observable, switchMap, take, tap } from 'rxjs';
import { IonicModule } from '@ionic/angular';
import {
  formattedTimeDifferenceUtil,
  GameDetailsModel,
  gameLevelMapper,
  MODAL_TOKEN,
  ModalProvider,
  preferredPositionMapper,
  PresentModalComponent
} from '@core';
import {
  GetUserLastGamesQueryHandler,
  UserDetailsQueryHandler,
  UserDetailsQueryResult
} from '../../application/handlers';
import { AppPreferredPlayerPosition, GameLevelEnum, Level } from '@core-http';
import { CardHistoryComponent, UserCommentsComponent } from '@fair-play';
import { OpenSingleChatCommandHandler } from '@messages';
import { PlayerRankComponent } from '@rank';
import { InfoTileComponent } from '../info-tile/info-tile.component';
import { ProfileSettingsComponent } from '../profile-settings/profile-settings.component';
import { LastGamesCarouselComponent } from '../last-games-carousel/last-games-carousel.component';
import { ProfileOptionsComponent } from '../profile-options/profile-options.component';
import { ProfileOptionAction } from '../profile-options/profile-option-action.enum';
import { ReportProfileModalComponent } from '../report-profile-modal/report-profile-modal.component';

@Component({
  selector: 'lib-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TitileNavbarComponent,
    BottomNavbarComponent,
    IonicModule,
    OrAvatarComponent,
    CardHistoryComponent,
    UserCommentsComponent,
    PlayerRankComponent,
    PageLayoutComponent,
    InfoTileComponent,
    ProfileSettingsComponent,
    LastGamesCarouselComponent,
    OrIconComponent
  ],
  providers: [UserDetailsQueryHandler, GetUserLastGamesQueryHandler, OpenSingleChatCommandHandler]
})
export class ProfileComponent {
  @ViewChild('highlightedBadge') private highlightedBadge?: ElementRef<HTMLElement>;

  private readonly userDetailsQueryHandler: UserDetailsQueryHandler = inject(UserDetailsQueryHandler);
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private readonly getUserLastGamesQueryHandler: GetUserLastGamesQueryHandler = inject(GetUserLastGamesQueryHandler);
  private readonly openSingleChatCommandHandler: OpenSingleChatCommandHandler = inject(OpenSingleChatCommandHandler);

  private readonly userDetails$: Observable<UserDetailsQueryResult> = this.modalProvider.showLoading$().pipe(
    switchMap(() => this.userDetailsQueryHandler.details()),
    tap(() => this.modalProvider.dismissLoading())
  );

  private readonly userLastGames$: Observable<GameDetailsModel[]> = this.getUserLastGamesQueryHandler.getLastGames();

  readonly userDetails: Signal<UserDetailsQueryResult | undefined> = toSignal(this.userDetails$);
  readonly userLastGames: Signal<GameDetailsModel[] | undefined> = toSignal(this.userLastGames$);

  readonly lastFiveGames: Signal<GameDetailsModel[]> = computed(() => (this.userLastGames() ?? []).slice(0, 5));

  readonly showHighlightedTooltip: WritableSignal<boolean> = signal<boolean>(false);

  readonly preferredPosition: Signal<string | null> = computed(() => {
    const position: AppPreferredPlayerPosition | null | undefined =
      this.userDetails()?.playerPreferences.preferredPosition;
    return position ? preferredPositionMapper[position] : null;
  });

  readonly preferredLevel: Signal<string | null> = computed(() => {
    const level: GameLevelEnum | Level | null | undefined = this.userDetails()?.playerPreferences.preferredLevel;
    return level ? gameLevelMapper[level] : null;
  });

  readonly navbarTitle: Signal<string> = computed(() => {
    const details: UserDetailsQueryResult | undefined = this.userDetails();
    if (!details) return '';
    return details.isMyProfile ? 'Mój profil' : 'Profil';
  });

  readonly canShowOptions: Signal<boolean> = computed(() => {
    const details: UserDetailsQueryResult | undefined = this.userDetails();
    return !!details && !details.isMyProfile;
  });

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.highlightedBadge?.nativeElement.contains(event.target as Node)) {
      this.showHighlightedTooltip.set(false);
    }
  }

  toggleHighlightedTooltip(): void {
    this.showHighlightedTooltip.update((visible: boolean) => !visible);
  }

  onMessageClicked(): void {
    const details: UserDetailsQueryResult | undefined = this.userDetails();
    if (details && !details.isMyProfile) this.sendMessage(details.userId, details.displayName);
  }

  calculatelastActive(date: string): string {
    return formattedTimeDifferenceUtil(new Date(date)).toLowerCase();
  }

  onDotsClicked(): void {
    if (!this.canShowOptions()) return;

    this.modalProvider
      .showModal$<ProfileOptionAction>({
        component: ProfileOptionsComponent,
        cssClass: 'modal-auto',
        initialBreakpoint: 1,
        breakpoints: [0, 1]
      })
      .pipe(take(1))
      .subscribe((action: ProfileOptionAction) => this.handleOptionAction(action));
  }

  sendMessage(userId: string, displayName?: string): void {
    this.openSingleChatCommandHandler.open(userId, displayName).subscribe();
  }

  private handleOptionAction(action: ProfileOptionAction): void {
    if (action === ProfileOptionAction.Message) this.onMessageClicked();
    if (action === ProfileOptionAction.Report) this.openReportModal();
  }

  private openReportModal(): void {
    const details: UserDetailsQueryResult | undefined = this.userDetails();
    if (!details || details.isMyProfile) return;

    this.modalProvider
      .showModal$<{ sent?: boolean }>({
        component: ReportProfileModalComponent,
        cssClass: 'present-modal',
        componentProps: {
          reportedUserId: details.userId,
          reportedDisplayName: details.displayName
        }
      })
      .pipe(take(1))
      .subscribe((result?: { sent?: boolean }) => {
        if (result?.sent) this.showReportSuccess();
      });
  }

  private showReportSuccess(): void {
    this.modalProvider
      .showModal$({
        component: PresentModalComponent,
        componentProps: {
          header: 'Zgłoszenie wysłane',
          message: 'Dziękujemy. Twoje zgłoszenie trafiło do naszego zespołu.',
          btnTxt: 'Zamknij'
        },
        cssClass: 'present-modal'
      })
      .pipe(take(1))
      .subscribe();
  }
}
